import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonContent,
  IonIcon,
  IonItem,
  IonText,
  IonToolbar,
  IonImg,
  IonAlert,
} from "@ionic/react";

import { tvOutline } from "ionicons/icons";

import AppHeader from "components/Header/AppHeader";

import { submitResults, updateProgress } from "services/quiz";
import { getNextPage, getPreviousPage } from "services/quiz/utils";
import { getRoundName, getRoundQuestionCount } from "services/util";
import Spacer from "components/spacer";

import { WSemit } from "services/socket";
import { MCQanswerEnum } from "services/util";
import RoundResults from "./components/ResultsRound";
import AggregateResults from "./components/ResultsAggregate";
import TableResults from "./components/ResultsTable";
import FinaliseButton from "components/FinaliseButton";
import ResultsWinner from "./components/ResultsWinner";
import CrosswordResults from "./components/ResultsCrossword";

import config from "config";
import Debug from "components/Debug";

interface props {
  teams: any;
  user: any;
  quizData: any;
  submitResults: Function;
  updateProgress: Function;
}

const Quiz: React.FC<props> = ({ teams, user, quizData, updateProgress }) => {
  const history = useHistory();

  const { quizConfig, progress } = quizData;
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [resultsAlertOpen, setResultsAlertOpen] = useState<boolean>(false);

  const [finaliseTrigger, setFinaliseTrigger] = React.useState(0);

  // const getProgress = () => progress;

  const haveResults = () => {
    const round = progress.round - 1;

    let roundResults = 0;
    teams.forEach((team: any) => {
      if (team.rounds[round]) {
        team.rounds[round].forEach((result: any) => {
          if (result) roundResults++;
        });
      }
    });
    return roundResults;
  };

  useEffect(() => {
    if (progress.section === "answer" && progress.page === "intro") {
      if (!haveResults()) {
        setResultsAlertOpen(true);
      } else {
        setResultsAlertOpen(false);
      }
    } else {
      setResultsAlertOpen(false);
    }

    //eslint-disable-next-line
  }, [progress, teams]);

  useEffect(() => {
    if (
      user.userDetails.availableQuizId !== quizData.quizId &&
      user.userDetails.availableQuizId > quizData.quizId
    ) {
      setAlertOpen(true);
    }
    //eslint-disable-next-line
  }, []);

  const getRoundCardHeader = () => {
    if (
      progress.round === quizConfig.format.rounds &&
      ["winner", "result", "puzzle", "cw-answer"].includes(progress.section) //two cw
    ) {
      return <IonCardSubtitle>Final</IonCardSubtitle>;
    } else {
      return (
        <IonCardSubtitle>
          Round {progress.round} -{" "}
          {getRoundName(quizConfig, progress.round - 1)}
        </IonCardSubtitle>
      );
    }
  };

  const questionDetails = () => {
    return quizConfig.rounds[progress.round - 1].questionDetails[
      progress.position
    ];
  };

  const enterResults = () => {
    history.push("/scoring/" + (progress.round - 1));
  };

  const isMediaSource = (question: any) => {
    if (
      question.template.includes("video") ||
      question.template.includes("audio")
    )
      return true;
    else return false;
  };

  const navButtons = () => {
    return (
      <IonToolbar>
        <IonItem lines="none">
          <div style={{ display: "block", width: "100%" }}>
            {!(
              (progress.section === "welcome" ||
                (progress.round === 1 && progress.section === "question")) &&
              progress.position === 0
            ) && (
              <IonButton
                fill="solid"
                color="primary"
                size="large"
                onClick={() => handlePreviousPage()}
                style={{ float: "left" }}
              >
                Prev
              </IonButton>
            )}
            <IonButton
              fill="solid"
              color="primary"
              size="large"
              onClick={() => {
                handleNextPage();
                if (
                  progress.round === quizData.quizConfig.format.rounds &&
                  progress.section === "result" &&
                  progress.page === "final" &&
                  (!quizData.resultsSubmitted || quizData.resultsAmended)
                ) {
                  setFinaliseTrigger(finaliseTrigger + 1);
                }
              }}
              style={{ float: "right" }}
            >
              Next
            </IonButton>
          </div>
        </IonItem>
      </IonToolbar>
    );
  };

  const cardHeader = () => {
    let cardTitle = "";
    if (progress.section === "welcome") {
      cardTitle = `Welcome!`;
    } else if (progress.section === "question" && progress.page === "intro") {
      cardTitle = `Intro - ${getRoundQuestionCount(
        quizConfig,
        progress.round - 1
      )} Questions`;
    } else if (
      progress.section === "question" &&
      progress.page === "question"
    ) {
      cardTitle = `Question ${progress.position + 1}`;
    } else if (progress.section === "crossword") {
      cardTitle = "Crossword Puzzle";
    } else if (progress.section === "breaker") {
      cardTitle = "Breaker Question";
    } else if (progress.section === "answer") {
      if (progress.page === "intro") {
        cardTitle = "Answers";
      } else {
        cardTitle = `Question/Answer ${progress.position + 1}`;
      }
    } else if (progress.section === "ad") {
      cardTitle = `Advertising`;
    } else if (progress.section === "crossword") {
      cardTitle = `Crossword`;
    } else if (progress.section === "result") {
      if (progress.position === 0) cardTitle = `Results`;
      else if (progress.page === "round") cardTitle = `Round Results`;
      else if (
        (progress.round > 1 && progress.page === "aggregate") ||
        (progress.round > 1 && progress.page === "aggregate-final")
      )
        cardTitle = `Aggregate Results`;
      else if (
        (progress.round === 1 && progress.page === "team") ||
        progress.position === 3
      ) {
        cardTitle = `Teams Results`;
      } else if (progress.page === "final") {
        cardTitle = `Final Results`;
      }
    } else if (progress.section === "winner") {
      cardTitle = `Winners`;
    }

    // console.log(progress.section === "result" && progress.page === "final");

    return (
      <>
        <IonCardHeader>
          {progress.section !== "welcome" ? getRoundCardHeader() : ""}
          <IonCardTitle>{cardTitle}</IonCardTitle>
        </IonCardHeader>
        {navButtons()}
      </>
    );
  };

  const handleNextPage = () => {
    const newState = getNextPage(
      quizData,
      user.userDetails,
      teams,
      quizData.progress
    );

    WSemit("updateProgress", {
      location: "quizNextButton",
      ...quizData.progress,
      ...newState,
    });
    updateProgress(newState);
  };

  const handlePreviousPage = () => {
    const newState = getPreviousPage(
      quizData,
      user.userDetails,
      teams,
      quizData.progress
    );
    WSemit("updateProgress", {
      location: "quizPrevButton",
      ...progress,
      ...newState,
    });
    updateProgress(newState);
  };

  return (
    <>
      <IonAlert
        isOpen={resultsAlertOpen}
        header="Alert"
        subHeader="Enter Results"
        message="You have not entered results for this round. Please enter results before continuing."
        buttons={[
          {
            text: "Continue anyway",
            role: "confirm",
          },
          {
            text: "OK",
            role: "cancel",
            handler: () => {
              enterResults();
            },
          },
        ]}
        onDidDismiss={() => setResultsAlertOpen(false)}
      ></IonAlert>
      <IonAlert
        isOpen={alertOpen}
        header="Warning"
        subHeader="A new quiz is available."
        message="You are about to play an old quiz. Do you want to continue?"
        buttons={[
          {
            text: "Continue Anyway",
            role: "cancel",
          },
          {
            text: "Cancel",
            role: "confirm",
            handler: () => {
              history.push("/dashboard");
              // setAlertOpen(false);
            },
          },
        ]}
        onDidDismiss={() => setAlertOpen(false)}
      ></IonAlert>
      <AppHeader title="Quiz" />
      <IonContent fullscreen className="ion-padding dashboard">
        <div className="container">
          {progress && (
            <IonCard>
              {cardHeader()}
              {progress.section === "welcome" && (
                // WELCOME
                <IonCardContent>
                  <Spacer size="30" />
                  <IonText>
                    <h1>Heads Up, Phones Down… It's time to Qwiz!</h1>
                  </IonText>
                </IonCardContent>
              )}
              {progress.section === "question" &&
                progress.page === "question" && (
                  // QUESTION
                  <IonCardContent>
                    <Spacer size="30" />
                    <IonText>
                      <h1>
                        {isMediaSource(
                          quizConfig.rounds[progress.round - 1].questionDetails[
                            progress.position
                          ]
                        ) && (
                          <IonIcon
                            style={{
                              paddingRight: "10px",
                              marginBottom: "-7px",
                            }}
                            icon={tvOutline}
                            size="large"
                          />
                        )}
                        {
                          quizConfig.rounds[progress.round - 1].questionDetails[
                            progress.position
                          ].description
                        }
                      </h1>
                    </IonText>

                    {questionDetails().formatType === "MCQ" &&
                      questionDetails().options.map(
                        (option: String, index: any) => (
                          <IonItem color="medium" key={index}>
                            {MCQanswerEnum[index]}. {option}
                          </IonItem>
                        )
                      )}
                  </IonCardContent>
                )}
              {progress.section === "crossword" &&
                progress.page === "question" && (
                  // CROSSWORD PUZZLE
                  <IonCardContent>
                    <Spacer size="30" />
                    <IonText>
                      <h2>
                        {
                          quizConfig.puzzle.crosswordQuestions[
                            progress.round - 1
                          ].imagePosition
                        }{" "}
                        {quizConfig.puzzle.crosswordQuestions[
                          progress.round - 1
                        ].direction === "x"
                          ? "Across"
                          : "Down"}
                      </h2>
                      <h1>
                        {
                          quizConfig.puzzle.crosswordQuestions[
                            progress.round - 1
                          ].question
                        }
                      </h1>
                    </IonText>
                  </IonCardContent>
                )}
              {progress.section === "breaker" && (
                // BREAKER QUESTION
                <IonCardContent>
                  <Spacer size="30" />
                  <IonText>
                    <h1>{quizConfig.breaker.question}</h1>
                  </IonText>
                  {progress.page === "answer-answer" && (
                    <IonItem color={"success"}>
                      {quizConfig.breaker.answer}
                    </IonItem>
                  )}
                </IonCardContent>
              )}
              {progress.section === "answer" && progress.page !== "intro" && (
                // ANSWER
                <IonCardContent>
                  <Spacer size="30" />
                  <IonText>
                    <h1>{questionDetails().description}</h1>
                  </IonText>
                  <Spacer size="15" />
                  <IonText>
                    <h5>Difficulty: {questionDetails().difficulty}</h5>
                  </IonText>
                  <Spacer size="15" />
                  {progress.page === "answer" ? (
                    <>
                      {questionDetails().formatType === "MCQ" &&
                        questionDetails().options.map(
                          (option: String, index: any) => (
                            <IonItem
                              color={
                                option === questionDetails().answer
                                  ? "success"
                                  : "light"
                              }
                              key={index}
                            >
                              {MCQanswerEnum[index]}. {option}
                            </IonItem>
                          )
                        )}
                      {questionDetails().formatType === "single" && (
                        <IonItem color="success">
                          {questionDetails().answer}
                        </IonItem>
                      )}
                    </>
                  ) : (
                    <>
                      {questionDetails().formatType === "MCQ" &&
                        questionDetails().options.map(
                          (option: String, index: any) => (
                            <IonItem color="medium" key={index}>
                              {MCQanswerEnum[index]}. {option}
                            </IonItem>
                          )
                        )}
                    </>
                  )}
                </IonCardContent>
              )}
              {progress.section === "crossword" &&
                progress.page !== "question" && (
                  <>
                    {progress.page === "intro" && (
                      <IonCardContent>
                        <Spacer size="30" />
                        <IonText>
                          <h1>Crossword Answers</h1>
                        </IonText>
                      </IonCardContent>
                    )}
                    {progress.page === "answer" && (
                      <IonCardContent>
                        <Spacer size="30" />
                        <IonText>
                          <h1>Crossword Answer</h1>
                        </IonText>
                        <IonImg
                          src={
                            config.MEDIA_HOST +
                            "media/" +
                            quizConfig.puzzle.completeCrosswordImage
                          }
                          alt="crossword answer"
                          style={{ maxWidth: "400px", margin: "0 auto" }}
                        />
                      </IonCardContent>
                    )}
                  </>
                )}
              {progress.section === "result" && (
                <>
                  {progress.page === "crossword" && (
                    <IonCardContent>
                      <Spacer size="30" />
                      <IonText>
                        <h1>Crossword Results</h1>
                      </IonText>
                      <CrosswordResults />
                    </IonCardContent>
                  )}
                  {progress.page === "intro" && (
                    <IonCardContent>
                      <Spacer size="30" />
                      <IonText>
                        <h1>R{progress.round} Results</h1>
                      </IonText>
                    </IonCardContent>
                  )}
                  {progress.page === "round" && (
                    <IonCardContent>
                      <Spacer size="30" />
                      <IonText>
                        <h1>R{progress.round} Overall</h1>
                      </IonText>
                      <RoundResults />
                    </IonCardContent>
                  )}
                  {progress.page === "aggregate" && (
                    <IonCardContent>
                      <Spacer size="30" />
                      <IonText>
                        <h1>R{progress.round} Aggregate</h1>
                      </IonText>
                      <AggregateResults />
                    </IonCardContent>
                  )}
                  {progress.page === "aggregate-final" && (
                    <IonCardContent>
                      <Spacer size="30" />
                      <IonText>
                        <h1>Final Aggregate</h1>
                      </IonText>
                      <AggregateResults />
                    </IonCardContent>
                  )}
                  {progress.page === "team" && (
                    <IonCardContent>
                      <Spacer size="30" />
                      <IonText>
                        <h1>R{progress.round} Team Table</h1>
                      </IonText>
                      <TableResults />
                    </IonCardContent>
                  )}
                  {progress.page === "final" && (
                    <IonCardContent>
                      <Spacer size="30" />
                      <IonText>
                        <h1>Final Team Table</h1>
                      </IonText>
                      <TableResults />
                    </IonCardContent>
                  )}
                </>
              )}
              {progress.section === "winner" && (
                <IonCardContent>
                  <Spacer size="30" />
                  {progress.page === "intro" ? (
                    <>
                      <IonText>
                        <h1>Intro</h1>
                      </IonText>
                      <Spacer size="30" />
                    </>
                  ) : (
                    <ResultsWinner />
                  )}
                </IonCardContent>
              )}
              <IonToolbar>
                <IonItem lines="none">
                  <div>
                    {progress.section !== "welcome" && (
                      <IonButton
                        fill="solid"
                        color="warning"
                        size="default"
                        onClick={() => enterResults()}
                      >
                        Enter Results
                      </IonButton>
                    )}
                    {progress.round >= quizConfig.format.rounds &&
                      (progress.section === "winner" ||
                        (progress.section === "result" &&
                          ["final", "aggregate-final"].includes(
                            progress.page
                          ))) &&
                      (!quizData.resultsSubmitted ||
                        quizData.resultsAmended) && (
                        <FinaliseButton trigger={finaliseTrigger} />
                      )}
                  </div>
                </IonItem>
                <Spacer size="30" />
              </IonToolbar>
            </IonCard>
          )}
          <Debug />
        </div>
      </IonContent>
    </>
  );
};

const mapStateToProps = (state: any) => ({
  user: state.user,
  quizData: state.quiz,
  teams: state.teams.teams,
});

export default connect(mapStateToProps, {
  submitResults,
  updateProgress,
})(Quiz);
